/**
 * THE BOOKS, AT BOTH LEVELS. D-EZ-72.
 *
 * Howard, 20 Aug 2026, looking at THE GARAGE'S BOOKS on the career page:
 * *"the P&L on the right shows their driver but shouldnt it be the name of the
 * team?"*
 *
 * ===========================================================================
 * HALF RIGHT, AND THE HALF HE WAS RIGHT ABOUT WAS MISSING ENTIRELY
 * ===========================================================================
 *
 * The rows are drivers because in this game's money rules THE CAR IS THE TEAM.
 * `seasonFinance.ts` quotes the guide verbatim and does not match it by
 * accident:
 *
 *     "A Team can have only one Driver."
 *     "A Team Owner may own up to three teams (and a total of three Drivers)."
 *     "Teams are always treated separately in the rules for all game functions
 *      … A Team Owner of two or three Teams cannot switch his money from one of
 *      his Teams to the others, for example."
 *
 * So our SHOP is Sagliano's team OWNER and each of its three cars is one of his
 * single-driver TEAMS. A shop cannot bail out its third car with the first car's
 * winnings, and a per-car row is the only honest way to show that.
 *
 * ⚠ BUT THE SCREEN SAID "THE GARAGE'S BOOKS" OVER IT, AND THEN PRINTED THE
 * CHARTER RULE UNDERNEATH. Both of those are about the SHOP — D-031 folds a
 * charter on the sum of its three cars, cumulatively, across seasons — and
 * neither was anywhere on the page. A reader looking at fifty-three car rows
 * under a garage heading and a charter footnote has been invited to read one as
 * the other, which is exactly what happened.
 *
 * Nothing here changes a number. It adds the level that was missing and labels
 * the one that was already there.
 */

import type { CareerSeason } from '../lib/careerRunner.ts';

export interface CarBook {
  driverId: string;
  driver: string;
  /** The shop that owns this car, or null for a privateer. */
  shop: string | null;
  incomeMb: number;
  expenseMb: number;
  balanceMb: number;
}

export interface ShopBook {
  teamId: string;
  shop: string;
  /** The drivers whose books are added up here, in the order the shop lists them. */
  cars: string[];
  incomeMb: number;
  expenseMb: number;
  balanceMb: number;
  /** How many of its cars finished the season in the red. */
  carsInTheRed: number;
}

/** `car-drv-012` → `drv-012`. The finance id carries the prefix; nothing else does. */
function driverIdOf(financeTeamId: string): string {
  return financeTeamId.replace(/^car-/, '');
}

/** driverId → the shop that runs him. Absent means he runs his own car. */
function shopsByDriver(career: CareerSeason): Map<string, string> {
  const out = new Map<string, string>();
  for (const team of career.teams) for (const id of team.driverIds) out.set(id, team.name);
  return out;
}

function nameOf(career: CareerSeason, driverId: string): string {
  return (
    career.season.finalStandings.byDriver[driverId]?.name ??
    career.roster.find((c) => c.driverId === driverId)?.name ??
    driverId
  );
}

/** Every car's book, richest first, with the shop it belongs to. */
export function carBooks(career: CareerSeason): CarBook[] {
  const shopOf = shopsByDriver(career);
  return career.report.teams
    .map((t) => {
      const driverId = driverIdOf(t.teamId);
      return {
        driverId,
        driver: nameOf(career, driverId),
        shop: shopOf.get(driverId) ?? null,
        incomeMb: t.incomeMb,
        expenseMb: t.expenseMb,
        balanceMb: t.balanceMb,
      };
    })
    .sort((a, b) => b.balanceMb - a.balanceMb);
}

/**
 * The fourteen shops, each the sum of its cars.
 *
 * ⚠ SUMMED, NOT STORED, AND THAT IS THE WHOLE POINT. There is no shop account
 * in the ledger — there cannot be one, because money never moves between a
 * shop's cars — so a shop's position is arithmetic over its three books and
 * nothing else. `settleTheGarage` computes it exactly this way when it decides
 * whether a charter folds, so this table and that rule cannot drift.
 *
 * ⚠ AND A PRIVATEER IS NOT A SHOP. A man running his own car has books and no
 * charter; putting him in here as a one-car shop would invent an organisation
 * and would put him at risk of a rule that cannot reach him.
 */
export function shopBooks(career: CareerSeason): ShopBook[] {
  const byDriver = new Map(career.report.teams.map((t) => [driverIdOf(t.teamId), t]));

  return career.teams
    .map((team) => {
      let incomeMb = 0;
      let expenseMb = 0;
      let balanceMb = 0;
      let carsInTheRed = 0;
      const cars: string[] = [];
      for (const driverId of team.driverIds) {
        cars.push(nameOf(career, driverId));
        const book = byDriver.get(driverId);
        if (!book) continue;
        incomeMb += book.incomeMb;
        expenseMb += book.expenseMb;
        balanceMb += book.balanceMb;
        if (book.balanceMb < 0) carsInTheRed += 1;
      }
      return { teamId: team.teamId, shop: team.name, cars, incomeMb, expenseMb, balanceMb, carsInTheRed };
    })
    .sort((a, b) => b.balanceMb - a.balanceMb);
}

/** How many cars run without a charter, so the page can say what it left out. */
export function privateers(career: CareerSeason): number {
  const chartered = new Set(career.teams.flatMap((t) => t.driverIds));
  return career.report.teams.filter((t) => !chartered.has(driverIdOf(t.teamId))).length;
}
